import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { db } from '../../services/supabase';
import toast from 'react-hot-toast';
import { FiCheck, FiX } from 'react-icons/fi';
import TicketIcon from '../common/icons/TicketIcon';
import WarningIcon from '../common/icons/WarningIcon';
import SparklesIcon from '../common/icons/SparklesIcon';
import { calculateOrderPricing } from '../../utils/order';

const sections = ['A', 'B', 'C', 'D', 'E'];
const ticketsPerSection = 200;

const TicketSelector = ({ selectedTickets, onTicketsChange, sessionToken, ticketPrice = 5 }) => {
  const queryClient = useQueryClient();
  const [isSyncingSelection, setIsSyncingSelection] = useState(false);

  // Fetch tickets with real-time updates
  const { data: tickets, isLoading, error, refetch } = useQuery({
    queryKey: ['tickets'],
    queryFn: async () => {
      const result = await db.tickets.getAll();
      return result ?? [];
    },
    refetchInterval: 1000, // Takmer okamžitá synchronizácia holdov medzi zariadeniami
    staleTime: 2000, // Dáta sú staré po 2 sekundách
  });

  // Funkcia na získanie statusu lístka (musí byť definovaná pred použitím)
  const getTicketStatus = (ticketNumber) => {
    const ticket = tickets?.find(t => t.ticket_number === ticketNumber);
    if (!ticket) return 'loading';
    
    if (selectedTickets.includes(ticketNumber)) return 'selected';

    const activeHolds = Array.isArray(ticket.ticket_holds)
      ? ticket.ticket_holds.filter((hold) => new Date(hold.expires_at).getTime() > Date.now())
      : [];
    const holdByOther = activeHolds.some((hold) => hold.session_token !== sessionToken);
    if (holdByOther) return 'held';
    
    // Najprv skontrolujeme status z order_tickets relácie (najpresnejší)
    const orderTicket = ticket.order_tickets?.[0];
    if (orderTicket) {
      const orderStatus = orderTicket.order?.status;
      if (orderStatus === 'paid') return 'paid';
      if (orderStatus === 'pending') return 'reserved';
    }
    
    // Potom skontrolujeme priamy status lístka (fallback)
    if (ticket.status === 'paid') return 'paid';
    if (ticket.status === 'reserved') return 'reserved';
    if (ticket.status === 'drawn') return 'drawn';
    
    // Ak má lístok drawn_at, je vyžrebovaný
    if (ticket.drawn_at) return 'drawn';
    
    // Inak je dostupný
    return ticket.status || 'available';
  };

  const syncSelection = async (nextSelection, successMessage) => {
    if (!sessionToken) {
      toast.error('Nie je možné rezervovať lístky. Obnovte stránku.');
      return;
    }

    try {
      setIsSyncingSelection(true);
      await db.tickets.syncHolds(sessionToken, nextSelection, 60);
      onTicketsChange(nextSelection);
      if (successMessage) {
        toast.success(successMessage);
      }
      await queryClient.invalidateQueries({ queryKey: ['tickets'] });
    } catch (syncError) {
      toast.error(syncError.message || 'Nepodarilo sa rezervovať lístok');
      await refetch();
    } finally {
      setIsSyncingSelection(false);
    }
  };

  const toggleTicket = async (ticketNumber) => {
    if (isSyncingSelection) return;

    const ticket = tickets?.find(t => t.ticket_number === ticketNumber);
    
    if (!ticket) {
      toast.error('Lístok neexistuje');
      return;
    }
    
    // Kontrolujeme status pomocou getTicketStatus funkcie
    const status = getTicketStatus(ticketNumber);
    
    if (status !== 'available' && status !== 'selected') {
      if (status === 'held') {
        toast.error('Lístok práve drží iný zákazník');
      } else if (status === 'reserved') {
        toast.error('Lístok je rezervovaný v inej objednávke');
      } else if (status === 'paid') {
        toast.error('Lístok je už predaný');
      } else if (status === 'drawn') {
        toast.error('Lístok je už vyžrebovaný');
      } else {
        toast.error('Lístok nie je dostupný');
      }
      return;
    }

    if (selectedTickets.includes(ticketNumber)) {
      const nextSelection = selectedTickets.filter(t => t !== ticketNumber);
      await syncSelection(nextSelection, `Lístok ${ticketNumber} odobraný`);
      return;
    }

    const nextSelection = [...selectedTickets, ticketNumber];
    await syncSelection(nextSelection, `Lístok ${ticketNumber} vybraný`);
  };

  const clearSelection = async () => {
    if (isSyncingSelection) return;
    await syncSelection([], 'Výber vymazaný');
  };

  const getTicketClassName = (status) => {
    // Základná trieda upravená pre mobilnú responzivitu
    const baseClass = 'relative w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center text-xs md:text-sm font-bold transition-all duration-200 cursor-pointer hover:scale-105 shadow-ticket';
    
    switch (status) {
      case 'available':
        return `${baseClass} bg-gradient-to-br from-primary-500 to-primary-600 text-slate-950 hover:from-primary-400 hover:to-primary-500 hover:shadow-ticket-hover`;
      case 'selected':
        return `${baseClass} bg-gradient-to-br from-success-500 to-success-600 text-slate-950 hover:from-success-400 hover:to-success-500 ring-2 ring-success-300 animate-pulse`;
      case 'reserved':
        return `${baseClass} bg-gradient-to-br from-warning-500 to-warning-600 text-slate-950 cursor-not-allowed opacity-75`;
      case 'held':
        return `${baseClass} bg-gradient-to-br from-sky-500 to-sky-600 text-slate-950 cursor-not-allowed opacity-80`;
      case 'paid':
        return `${baseClass} bg-gradient-to-br from-slate-700 to-slate-600 text-slate-300 cursor-not-allowed opacity-70`;
      case 'drawn':
        return `${baseClass} bg-gradient-to-br from-secondary-500 to-secondary-600 text-white cursor-not-allowed opacity-80 animate-pulse`;
      default:
        return `${baseClass} bg-slate-800 text-slate-500 animate-pulse cursor-wait`;
    }
  };

  const stats = tickets ? {
    total: tickets.length,
    available: tickets.filter(t => getTicketStatus(t.ticket_number) === 'available').length,
    reserved: tickets.filter(t => getTicketStatus(t.ticket_number) === 'reserved').length,
    held: tickets.filter(t => getTicketStatus(t.ticket_number) === 'held').length,
    paid: tickets.filter(t => getTicketStatus(t.ticket_number) === 'paid').length,
    drawn: tickets.filter(t => getTicketStatus(t.ticket_number) === 'drawn').length,
    selected: selectedTickets.length
  } : {};
  const selectionPricing = calculateOrderPricing(selectedTickets.length, ticketPrice);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary-400 mx-auto mb-4"></div>
          <p className="text-slate-300 text-lg">Načítavam lístky...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-12">
        <div className="text-error-400 text-6xl mb-4 flex justify-center">
          <WarningIcon className="text-5xl" aria-hidden="true" />
        </div>
        <h3 className="text-xl font-semibold text-primary-100 mb-2">Chyba pri načítaní lístkov</h3>
        <p className="text-slate-400 mb-6">Skúste stránku načítať znova alebo kontaktujte podporu</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="text-center">
        <h2 className="text-3xl font-bold text-primary-100 mb-2">
          Výber lístkov
        </h2>
        <p className="text-slate-300 mb-6">
          Vyberte si lístky pre tombolu kliknutím
        </p>
        <p className="text-primary-200 text-lg font-semibold mb-3">Akcia: kúp 5 lístkov, 1 máš zdarma.</p>
        
        {/* Statistics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
          <div className="bg-slate-900/80 border border-slate-800 rounded-lg p-3">
            <div className="text-2xl font-bold text-primary-100">{stats.total}</div>
            <div className="text-xs text-slate-400">Celkom</div>
          </div>
          <div className="bg-success-500/10 border border-success-500/30 rounded-lg p-3">
            <div className="text-2xl font-bold text-success-200">{stats.available}</div>
            <div className="text-xs text-success-100">Dostupné</div>
          </div>
          <div className="bg-error-500/10 border border-error-500/30 rounded-lg p-3">
            <div className="text-2xl font-bold text-error-200">{stats.selected}</div>
            <div className="text-xs text-error-100">Vybrané</div>
          </div>
          <div className="bg-warning-500/10 border border-warning-500/30 rounded-lg p-3">
            <div className="text-2xl font-bold text-warning-200">{stats.reserved}</div>
            <div className="text-xs text-warning-100">Rezervované</div>
          </div>
          <div className="bg-slate-900/80 border border-slate-800 rounded-lg p-3">
            <div className="text-2xl font-bold text-slate-200">{stats.paid}</div>
            <div className="text-xs text-slate-400">Predané</div>
          </div>
          <div className="bg-secondary-500/10 border border-secondary-500/30 rounded-lg p-3">
            <div className="text-2xl font-bold text-secondary-200">{stats.drawn}</div>
            <div className="text-xs text-secondary-100">Vyžrebované</div>
          </div>
        </div>
      </div>

      {/* Selection Actions */}
      <div className="card backdrop-blur-sm">
        <div className="flex flex-wrap gap-3">
          <button
            onClick={clearSelection}
            disabled={selectedTickets.length === 0 || isSyncingSelection}
            className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Vymazať výber ({selectedTickets.length})
          </button>
        </div>
      </div>

      {/* Tickets Grid */}
      <div className="card backdrop-blur-sm">
        <h3 className="text-lg font-semibold text-primary-100 mb-4 flex items-center gap-2">
          <TicketIcon className="text-xl text-primary-300" aria-hidden="true" />
          <span>Lístky</span>
        </h3>
        
        <div className="space-y-8">
          {sections.map(section => (
            <motion.div 
              key={section}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <h4 className="text-xl font-semibold text-slate-200 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-primary-500 text-slate-950 rounded-full flex items-center justify-center text-sm shadow-glow">
                  {section}
                </span>
                Sekcia {section}
              </h4>
              
              <div className="grid grid-cols-4 sm:grid-cols-8 md:grid-cols-12 lg:grid-cols-15 xl:grid-cols-20 gap-2 sm:gap-2.5">
                {Array.from({ length: ticketsPerSection }, (_, i) => {
                  const ticketNumber = `${section}${i + 1}`;
                  const status = getTicketStatus(ticketNumber);
                  
                  return (
                    <motion.button
                      key={ticketNumber}
                      onClick={() => toggleTicket(ticketNumber)}
                      disabled={isSyncingSelection || status === 'held' || status === 'reserved' || status === 'paid' || status === 'drawn'}
                      className={getTicketClassName(status)}
                      title={`Lístok ${ticketNumber} - ${status}`}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.2 }}
                    >
                      {status === 'selected' && <FiCheck className="w-4 h-4" />}
                      {(status === 'held' || status === 'reserved' || status === 'paid' || status === 'drawn') && <FiX className="w-4 h-4" />}
                      {status === 'available' && (i + 1)}
                      {status === 'loading' && '...'}
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Selected Tickets Summary */}
      <AnimatePresence>
        {selectedTickets.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-gradient-to-r from-primary-500/20 via-secondary-500/20 to-primary-500/20 border border-primary-500/30 rounded-2xl p-6 text-slate-100 backdrop-blur-sm"
          >
            <h3 className="text-xl font-semibold mb-4 text-primary-100 flex items-center gap-2">
              <SparklesIcon className="text-xl text-primary-300" aria-hidden="true" />
              <span>Váš výber</span>
            </h3>
            <div className="flex flex-wrap gap-2 mb-4">
              {selectedTickets.map(ticket => (
                <span 
                  key={ticket}
                  className="bg-primary-500/20 border border-primary-500/40 px-3 py-1 rounded-full text-sm font-medium"
                >
                  {ticket}
                </span>
              ))}
            </div>
            <div className="text-2xl font-bold text-primary-100">
              {selectedTickets.length} {selectedTickets.length === 1 ? 'lístok' : 'lístky'} • {selectionPricing.totalAmount}€
            </div>
            {selectionPricing.ticketCount > 0 && selectionPricing.ticketCount % 6 === 5 && (
              <div className="text-sm text-warning-200 mt-1 font-semibold">
                Vyber si ešte 1 lístok a máš 1 zdarma.
              </div>
            )}
            {selectionPricing.freeTickets > 0 && (
              <div className="text-sm text-success-200 mt-1">
                Zľava: {selectionPricing.freeTickets} lístok zdarma
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TicketSelector;
